import { useEffect, useMemo, useRef, useState } from "react"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { readSheet } from "read-excel-file/browser"

import SelectDescription from "components/forms/select-description"
import useBranchRepositories from "repositories/branch"
import {
  finalizeMasterStockYearly,
  getMasterStockYearlyStatus,
  importMasterStockYearlyBatch,
  resetMasterStockYearly,
  resolveMasterStockYearlyCodes,
} from "repositories/master-stock-yearly"

// File "Master Stock HO": JANM..DECM = stok masuk per bulan, JANK..DECK = stok
// keluar per bulan, satuan per baris (LSN = lusin x12 pcs). Dibaca di browser,
// dikirim per-chunk JSON (protokol product-unit-stocks/import-batch).
const CHUNK_SIZE = 400

const MONTH_IN_COLUMNS = ["JANM", "FEBM", "MARM", "APRM", "MAYM", "JUNM", "JULM", "AUGM", "SEPM", "OCTM", "NOVM", "DECM"]
const MONTH_OUT_COLUMNS = ["JANK", "FEBK", "MARK", "APRK", "MAYK", "JUNK", "JULK", "AUGK", "SEPK", "OCTK", "NOVK", "DECK"]

const makeBatchId = () =>
  `MS${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`

const formatNumber = (n) => new Intl.NumberFormat("id-ID").format(Number(n || 0))

const downloadCsv = (filename, rows) => {
  const escape = (v) => {
    const s = String(v ?? "")
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const csv = rows.map((row) => row.map(escape).join(",")).join("\r\n")
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// Parse file Master Stock HO -> rows utk API: {code, satuan, awal, akhir, masuk, keluar}
const parseMasterStockFile = async (file) => {
  const sheet = await readSheet(file)
  if (!sheet.length) return []

  const headers = sheet[0].map((h) =>
    h === null || h === undefined ? "" : String(h).trim().toUpperCase()
  )
  const col = (name) => headers.indexOf(name)

  const kodeIdx = col("KODE")
  if (kodeIdx === -1) {
    throw new Error('Kolom "KODE" tidak ditemukan di header file. Pastikan file Master Stock HO yang benar.')
  }

  const satuanIdx = col("SATUAN")
  const awalIdx = col("AWAL")
  const akhirIdx = col("AKHIR")
  const inIdx = MONTH_IN_COLUMNS.map(col)
  const outIdx = MONTH_OUT_COLUMNS.map(col)

  if (inIdx.some((i) => i === -1) || outIdx.some((i) => i === -1)) {
    throw new Error("Kolom bulanan JANM..DECM / JANK..DECK tidak lengkap di header file.")
  }

  const num = (v) => {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  }

  // KODE duplikat (beda spasi saja) DIGABUNG di sini — dijumlahkan sebelum
  // dipecah jadi chunk, supaya tidak ada pasangan duplikat yang terpisah chunk
  // (ledger backend bisa men-drop diam-diam kalau product+bulan sama).
  const rows = []
  const indexByCompactCode = new Map()
  for (let i = 1; i < sheet.length; i++) {
    const r = sheet[i]
    if (!r) continue
    const code = String(r[kodeIdx] ?? "").trim()
    if (!code) continue

    const masuk = {}
    const keluar = {}
    for (let m = 0; m < 12; m++) {
      const inVal = num(r[inIdx[m]])
      const outVal = num(r[outIdx[m]])
      if (inVal !== 0) masuk[m + 1] = inVal
      if (outVal !== 0) keluar[m + 1] = outVal
    }

    const compact = code.toUpperCase().replace(/\s+/g, "")
    if (indexByCompactCode.has(compact)) {
      const row = rows[indexByCompactCode.get(compact)]
      row.awal += num(awalIdx === -1 ? 0 : r[awalIdx])
      row.akhir += num(akhirIdx === -1 ? 0 : r[akhirIdx])
      for (const [m, v] of Object.entries(masuk)) row.masuk[m] = (row.masuk[m] || 0) + v
      for (const [m, v] of Object.entries(keluar)) row.keluar[m] = (row.keluar[m] || 0) + v
      continue
    }

    indexByCompactCode.set(compact, rows.length)
    rows.push({
      code,
      satuan: String(r[satuanIdx] ?? "").trim(),
      awal: num(awalIdx === -1 ? 0 : r[awalIdx]),
      akhir: num(akhirIdx === -1 ? 0 : r[akhirIdx]),
      masuk,
      keluar,
    })
  }
  return rows
}

const emptyTotals = () => ({
  rowsMatched: 0,
  matchedByName: 0,
  matchedByLegacy: 0,
  ledgerRows: 0,
  orderItems: 0,
  stockInitRows: 0,
  duplicates: 0,
  unmatchedCodes: [],
  noPriceCodes: [],
})

const MasterStockYearlyImportPage = () => {
  const [file, setFile] = useState(null)
  const [outletId, setOutletId] = useState("")
  const [includeStockInit, setIncludeStockInit] = useState(false)
  const [asOfDate, setAsOfDate] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [progress, setProgress] = useState(null)
  const [result, setResult] = useState(null)
  const [status, setStatus] = useState(null)
  const cancelRef = useRef(false)

  const { data: outletSelection, isLoading: isLoadingOutletSelection } =
    useBranchRepositories({ paginate: false, stockScope: true })
  const outletOptions = useMemo(() => outletSelection || [], [outletSelection])

  const refreshStatus = async (targetOutletId) => {
    if (!targetOutletId) {
      setStatus(null)
      return
    }
    try {
      const data = await getMasterStockYearlyStatus({ outletId: targetOutletId })
      setStatus(data?.data || null)
    } catch {
      setStatus(null)
    }
  }

  useEffect(() => {
    refreshStatus(outletId)
  }, [outletId])

  const run = async (dryRun) => {
    if (!outletId) {
      toast.error("Silakan pilih outlet terlebih dahulu")
      return
    }
    if (!file) {
      toast.error("Silakan pilih file Excel terlebih dahulu")
      return
    }

    nProgress.start()
    setSubmitting(true)
    setResult(null)
    cancelRef.current = false

    const totals = emptyTotals()
    const errors = []
    let monthYearMap = null
    let finalizeData = null

    try {
      const parsed = await parseMasterStockFile(file)
      if (!parsed.length) {
        toast.error("File tidak berisi baris data")
        return
      }

      // Resolve semua KODE dulu (1 request), lalu gabung baris yang menunjuk
      // PRODUK yang sama (kode alias: satu via nama, satu via legacy mapping).
      // Tanpa ini, pasangan alias yang terpisah chunk bikin baris ledger
      // bulan yang sama tabrakan di backend dan salah satunya hilang.
      setProgress({ chunk: 0, totalChunks: 0, processedRows: 0, totalRows: parsed.length, phase: "resolve" })
      const resolveRes = await resolveMasterStockYearlyCodes({ codes: parsed.map((r) => r.code) })
      const puidByCode = resolveRes?.data?.resolved || {}

      const rows = []
      const indexByProduct = new Map()
      for (const row of parsed) {
        const key = puidByCode[row.code] || `?${row.code.toUpperCase().replace(/\s+/g, "")}`
        if (indexByProduct.has(key)) {
          const target = rows[indexByProduct.get(key)]
          target.awal += row.awal
          target.akhir += row.akhir
          for (const [m, v] of Object.entries(row.masuk)) target.masuk[m] = (target.masuk[m] || 0) + v
          for (const [m, v] of Object.entries(row.keluar)) target.keluar[m] = (target.keluar[m] || 0) + v
          continue
        }
        indexByProduct.set(key, rows.length)
        rows.push(row)
      }

      const totalChunks = Math.ceil(rows.length / CHUNK_SIZE)
      const batchId = makeBatchId()
      setProgress({ chunk: 0, totalChunks, processedRows: 0, totalRows: rows.length, phase: "upload" })

      let aborted = false
      for (let i = 0; i < totalChunks; i++) {
        if (cancelRef.current) {
          toast("Impor dibatalkan", { icon: "⏹️" })
          aborted = true
          break
        }

        const start = i * CHUNK_SIZE
        const end = Math.min(start + CHUNK_SIZE, rows.length)

        try {
          const response = await importMasterStockYearlyBatch({
            outletId,
            rows: rows.slice(start, end),
            chunkIndex: i,
            totalChunks,
            batchId,
            dryRun,
            includeStockInit,
            asOfDate: asOfDate || null,
          })
          const data = response?.data || {}
          monthYearMap = data.month_year_map || monthYearMap
          totals.rowsMatched += Number(data.rows_matched || 0)
          totals.matchedByName += Number(data.matched_by_name || 0)
          totals.matchedByLegacy += Number(data.matched_by_legacy || 0)
          totals.ledgerRows += Number(data.ledger_rows || 0)
          totals.orderItems += Number(data.order_items || 0)
          totals.stockInitRows += Number(data.stock_init_rows || 0)
          totals.duplicates += Number(data.rows_duplicate || 0)
          if (Array.isArray(data.unmatched_codes)) {
            totals.unmatchedCodes = totals.unmatchedCodes.concat(data.unmatched_codes)
          }
          if (Array.isArray(data.no_price_codes)) {
            totals.noPriceCodes = totals.noPriceCodes.concat(data.no_price_codes)
          }
        } catch (error) {
          const statusCode = error?.response?.status
          const msg = error?.response?.data?.message || error?.message || "Gagal mengirim chunk"
          if (statusCode === 409) {
            toast.error(msg)
            errors.push(msg)
            aborted = true
            break
          }
          errors.push(`Chunk ${i + 1} (baris ${start + 2}-${end + 1}): ${msg}`)
        }

        setProgress({ chunk: i + 1, totalChunks, processedRows: end, totalRows: rows.length, phase: "upload" })
      }

      if (!aborted && !dryRun) {
        setProgress((p) => ({ ...(p || {}), phase: "finalize" }))
        try {
          const fin = await finalizeMasterStockYearly({ outletId })
          finalizeData = fin?.data || null
        } catch (error) {
          errors.push(`Finalize gagal: ${error?.response?.data?.message || error?.message}`)
        }
      }

      setResult({
        dryRun,
        totals,
        errors,
        monthYearMap,
        finalize: finalizeData,
        totalRows: rows.length,
      })

      if (errors.length === 0) {
        toast.success(dryRun ? "Dry run selesai" : "Import selesai")
      } else {
        toast.error(`Selesai dengan ${errors.length} error`)
      }

      if (!dryRun) refreshStatus(outletId)
    } catch (error) {
      toast.error(error?.message || "Gagal membaca file Excel")
    } finally {
      nProgress.done()
      setSubmitting(false)
      setProgress(null)
    }
  }

  const handleReset = async () => {
    if (!outletId) {
      toast.error("Silakan pilih outlet terlebih dahulu")
      return
    }
    if (!window.confirm(
      "Hapus seluruh hasil import Master Stock untuk outlet ini?\n" +
      "(orders OMS-*, ledger IMPORT_IN/IMPORT_OUT, stock init IMPMV-*)"
    )) return

    nProgress.start()
    setSubmitting(true)
    try {
      const data = await resetMasterStockYearly({ outletId })
      const d = data?.data || {}
      toast.success(
        `Terhapus: ${formatNumber(d.orders)} order, ${formatNumber(d.order_items)} item, ` +
        `${formatNumber(d.ledger)} baris ledger, ${formatNumber(d.stock_init)} stock init`
      )
      setResult(null)
      refreshStatus(outletId)
    } catch (error) {
      toast.error(error?.response?.data?.message || "Gagal menghapus hasil import")
    } finally {
      nProgress.done()
      setSubmitting(false)
    }
  }

  const percent =
    progress && progress.totalRows
      ? Math.round((progress.processedRows / progress.totalRows) * 100)
      : 0

  const hasExisting = Boolean(status?.any)

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="space-y-1 mb-6">
        <h1 className="text-lg font-semibold text-neutral-900">
          Import Pergerakan Stok Setahun (Master Stock)
        </h1>
        <p className="text-xs text-neutral-600">
          Import file Excel "Master Stock HO" (kolom <code className="px-1 bg-neutral-100 rounded">JANM..DECM</code> = stok
          masuk, <code className="px-1 bg-neutral-100 rounded">JANK..DECK</code> = stok keluar per bulan).
          Stok masuk/keluar dicatat ke ledger (Laporan Master Stock), stok keluar juga dibuatkan
          order sintetis (Laporan Penjualan Setahun, qty + amount dari harga jual aktif).
        </p>
      </div>

      <div className="space-y-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl text-[11px] text-blue-900">
        <div className="font-semibold">Catatan penting</div>
        <ul className="pl-5 list-disc space-y-1">
          <li>
            Import bersifat <strong>history-only</strong>: stok berjalan (product unit stock)
            TIDAK diubah oleh pergerakan bulanan. Kolom <code>AKHIR</code> ditanam sebagai
            stock awal (STOCK_INIT) bila opsi di bawah dicentang.
          </li>
          <li>
            Satuan dikonversi otomatis ke PCS: <code>LSN</code> ×12, <code>PSG</code> ×2.
          </li>
          <li>
            Kolom bulan dipetakan rolling 12 bulan dari tanggal acuan (JAN..JUL = tahun acuan,
            AUG..DEC = tahun sebelumnya). <strong>Semua 12 bulan diimpor</strong> — termasuk
            bulan berjalan file. Sisa selisih (retur/koreksi tahunan) dicatat sebagai 1 baris
            penyesuaian bertanggal acuan.
          </li>
          <li>
            KODE dicocokkan ke <strong>nama Product Unit</strong> dulu (hanya produk aktif,
            pencocokan mengabaikan spasi), kalau tidak ketemu baru fallback ke Legacy Product
            Unit Mapping. Yang tetap tidak ketemu → di-skip (bisa di-download CSV-nya).
          </li>
          <li>
            Harga order sintetis = harga jual aktif terbaru outlet ini. Produk tanpa harga
            tetap ter-import dengan amount 0.
          </li>
          <li>
            <strong>Gunakan Preview (Dry Run)</strong> dulu. Re-import outlet yang sama akan
            ditolak — hapus hasil import lama dulu dengan tombol "Hapus Hasil Import".
          </li>
        </ul>
      </div>

      <div className="space-y-4 text-xs">
        <div className="space-y-2">
          <label className="block text-xs font-medium text-neutral-700">Outlet</label>
          <SelectDescription
            onChange={(value) => {
              setOutletId(value)
              setResult(null)
            }}
            selection={outletOptions}
            isLoading={isLoadingOutletSelection}
            value={outletId}
            keyValue={(value) => value.id}
            title={(value) => value.name}
            description={(value) => value.code || value.id}
            placeholder="Pilih Outlet"
          />
          {hasExisting && (
            <div className="flex items-center justify-between p-2 border border-amber-300 bg-amber-50 rounded-lg text-[11px] text-amber-900">
              <span>
                Outlet ini sudah punya hasil import: {formatNumber(status.orders)} order OMS,{" "}
                {formatNumber(status.ledger)} baris ledger IMPMV.
              </span>
              <button
                type="button"
                onClick={handleReset}
                disabled={submitting}
                className="shrink-0 ml-3 px-3 py-1.5 font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition disabled:opacity-60"
              >
                Hapus Hasil Import
              </button>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-medium text-neutral-700">File Excel (Master Stock HO)</label>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => {
              const f = e.target.files?.[0]
              setFile(f || null)
              setResult(null)
            }}
            disabled={submitting}
            className="block w-full text-xs text-neutral-700 border border-neutral-200 rounded-xl cursor-pointer file:mr-3 file:px-3 file:py-2 file:text-xs file:font-medium file:border-0 file:rounded-xl file:bg-brand-800 file:text-white hover:file:bg-brand-900 disabled:opacity-60"
          />
          {file && (
            <p className="text-[11px] text-neutral-500">
              Selected file: <span className="font-medium">{file.name}</span>
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-medium text-neutral-700">
            Tanggal acuan file (as of)
          </label>
          <input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            disabled={submitting}
            className="block w-full px-3 py-2 text-xs text-neutral-700 border border-neutral-200 rounded-xl"
          />
          <p className="text-[11px] text-neutral-500">
            Isi dengan <strong>tanggal file</strong> (mis. file "180726" → 2026-07-18).
            Menentukan mapping bulan→tahun dan tanggal baris penyesuaian. Kosong = hari ini.
          </p>
        </div>

        <label className="flex items-start space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeStockInit}
            onChange={(e) => setIncludeStockInit(e.target.checked)}
            disabled={submitting}
            className="mt-0.5"
          />
          <span className="text-[11px] text-neutral-700">
            Tanam kolom <code className="px-1 bg-neutral-100 rounded">AKHIR</code> sebagai stok
            awal outlet (STOCK_INIT). <strong>Biarkan kosong bila outlet sudah punya stok</strong>{" "}
            (mis. sudah import stock awal sebelumnya) — mencentang ini akan MENAMBAH stok outlet.
          </span>
        </label>

        {progress && (
          <div className="p-3 space-y-2 text-[11px] border rounded-xl border-neutral-200 bg-neutral-50">
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-700">
                {progress.phase === "finalize"
                  ? "Finalisasi (update total order + rebuild laporan harian)..."
                  : progress.phase === "resolve"
                  ? "Mencocokkan kode produk..."
                  : `Chunk ${progress.chunk} / ${progress.totalChunks}`}
              </span>
              <span className="text-neutral-500">
                {progress.processedRows} / {progress.totalRows} rows ({percent}%)
              </span>
            </div>
            <div className="w-full h-2 overflow-hidden rounded-full bg-neutral-200">
              <div className="h-full transition-all bg-brand-800" style={{ width: `${percent}%` }} />
            </div>
          </div>
        )}

        <div className="flex items-center space-x-3 pt-2">
          <button
            type="button"
            onClick={() => run(true)}
            disabled={submitting}
            className="inline-flex items-center px-5 py-3 text-white transition bg-neutral-500 rounded-xl active:hover:scale-90 disabled:opacity-60"
          >
            {submitting ? "Memproses..." : "Preview (Dry Run)"}
          </button>
          <button
            type="button"
            onClick={() => {
              if (!window.confirm(
                "Eksekusi import? Data akan masuk ke ledger stok + orders (penjualan setahun)."
              )) return
              run(false)
            }}
            disabled={submitting || hasExisting}
            className="inline-flex items-center px-5 py-3 text-white transition bg-brand-800 rounded-xl active:hover:scale-90 disabled:opacity-60"
          >
            {submitting ? "Memproses..." : "Import"}
          </button>
          {submitting && progress?.phase === "upload" && (
            <button
              type="button"
              onClick={() => { cancelRef.current = true }}
              className="px-4 py-3 text-xs border border-neutral-300 text-neutral-700 rounded-xl hover:bg-neutral-100"
            >
              Batal
            </button>
          )}
        </div>

        {result && <ResultSummary result={result} outletId={outletId} />}
      </div>
    </div>
  )
}

const ResultSummary = ({ result, outletId }) => {
  const { totals, errors, finalize, dryRun, monthYearMap, totalRows } = result
  const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"]

  return (
    <div className="mt-6 space-y-4 text-xs">
      <div className="p-4 border rounded-xl border-neutral-200 bg-neutral-50">
        <div className="font-semibold mb-2">Ringkasan {dryRun ? "(Dry Run)" : ""}</div>
        <div className="grid grid-cols-2 gap-2">
          <div>Total baris file</div><div className="font-medium">{formatNumber(totalRows)}</div>
          <div>Baris produk match</div>
          <div className="font-medium">
            {formatNumber(totals.rowsMatched)}{" "}
            <span className="text-neutral-500">
              (nama: {formatNumber(totals.matchedByName)}, legacy: {formatNumber(totals.matchedByLegacy)})
            </span>
          </div>
          <div>Kode tidak ketemu</div><div className="font-medium text-red-700">{formatNumber(totals.unmatchedCodes.length)}</div>
          <div>Baris duplikat di-skip</div><div className="font-medium">{formatNumber(totals.duplicates)}</div>
          <div>Baris ledger masuk/keluar</div><div className="font-medium">{formatNumber(totals.ledgerRows)}</div>
          <div>Item order penjualan</div><div className="font-medium">{formatNumber(totals.orderItems)}</div>
          <div>Produk keluar tanpa harga (amount 0)</div><div className="font-medium text-amber-700">{formatNumber(totals.noPriceCodes.length)}</div>
          <div>Produk stock init (AKHIR)</div><div className="font-medium">{formatNumber(totals.stockInitRows)}</div>
          {finalize && (
            <>
              <div>Order dibuat</div><div className="font-medium text-green-700">{formatNumber(finalize.orders)}</div>
              <div>Total qty terjual (PCS)</div><div className="font-medium text-green-700">{formatNumber(finalize.total_qty_pcs)}</div>
              <div>Total amount penjualan</div><div className="font-medium text-green-700">Rp {formatNumber(finalize.total_amount)}</div>
              <div>Hari agg penjualan di-rebuild</div><div className="font-medium">{formatNumber(finalize.agg_days_rebuilt)}</div>
            </>
          )}
        </div>
      </div>

      {monthYearMap && (
        <div className="p-4 border rounded-xl border-neutral-200">
          <div className="font-semibold mb-2">Mapping bulan → tahun-bulan</div>
          <div className="grid grid-cols-4 gap-1">
            {Object.entries(monthYearMap).map(([idx, ym]) => (
              <div key={idx} className="flex justify-between px-2 py-1 bg-neutral-50 rounded">
                <span className="text-neutral-600">{monthNames[Number(idx) - 1]}</span>
                <span className="font-medium">{ym}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {totals.unmatchedCodes.length > 0 && (
        <div className="p-4 border rounded-xl border-amber-300 bg-amber-50">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="font-semibold text-amber-900">
              Kode tidak ditemukan di Legacy Mapping ({totals.unmatchedCodes.length})
            </div>
            <button
              type="button"
              onClick={() =>
                downloadCsv(`unmatched-master-stock-${outletId}.csv`, [
                  ["legacy_product_name"],
                  ...totals.unmatchedCodes.map((code) => [code]),
                ])
              }
              className="shrink-0 px-3 py-1.5 text-[11px] font-medium text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 transition"
            >
              Download CSV
            </button>
          </div>
          <div className="max-h-48 overflow-auto pl-4">
            <ul className="list-disc space-y-1 text-amber-900">
              {totals.unmatchedCodes.slice(0, 200).map((code, idx) => (
                <li key={idx}>{code}</li>
              ))}
              {totals.unmatchedCodes.length > 200 && (
                <li className="italic">... dan {totals.unmatchedCodes.length - 200} kode lainnya</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {totals.noPriceCodes.length > 0 && (
        <div className="p-4 border rounded-xl border-orange-300 bg-orange-50">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="font-semibold text-orange-900">
              Produk keluar TANPA harga di outlet ini — amount penjualan 0 ({totals.noPriceCodes.length})
            </div>
            <button
              type="button"
              onClick={() =>
                downloadCsv(`no-price-master-stock-${outletId}.csv`, [
                  ["product_name"],
                  ...totals.noPriceCodes.map((code) => [code]),
                ])
              }
              className="shrink-0 px-3 py-1.5 text-[11px] font-medium text-orange-900 bg-white border border-orange-300 rounded-lg hover:bg-orange-100 transition"
            >
              Download CSV
            </button>
          </div>
          <div className="max-h-48 overflow-auto pl-4">
            <ul className="list-disc space-y-1 text-orange-900">
              {totals.noPriceCodes.slice(0, 200).map((code, idx) => (
                <li key={idx}>{code}</li>
              ))}
              {totals.noPriceCodes.length > 200 && (
                <li className="italic">... dan {totals.noPriceCodes.length - 200} produk lainnya</li>
              )}
            </ul>
          </div>
          <div className="mt-2 text-[11px] text-orange-800">
            Qty tetap ter-import ke laporan penjualan, hanya amount-nya 0. Tambahkan harga
            produk untuk outlet ini dulu bila amount-nya harus terisi, lalu hapus hasil import
            dan import ulang.
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div className="p-3 text-[11px] border rounded-xl border-red-200 bg-red-50 text-red-700 max-h-40 overflow-auto">
          <div className="font-semibold mb-1">Error ({errors.length}):</div>
          <ul className="pl-4 space-y-1 list-disc">
            {errors.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default MasterStockYearlyImportPage
